import type { ErrorEnvelope } from "./types";

/**
 * Normalized API error. Components switch on `code` (stable SCREAMING_SNAKE),
 * never on `message`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export const LOGIN_PATH = "/api/auth/login";

/**
 * The single registered handler invoked on any 401 (except the login path).
 * Wired by AuthProvider to flip auth state + clear the query cache + redirect.
 * Kept as a module-level slot (not a React context) so the bare API client and
 * the upload XHR can call it without importing AuthProvider (avoids a cycle).
 */
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function registerUnauthorizedHandler(fn: UnauthorizedHandler): void {
  unauthorizedHandler = fn;
}

export function getUnauthorizedHandler(): UnauthorizedHandler | null {
  return unauthorizedHandler;
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as { error: unknown }).error === "object" &&
    (value as { error: unknown }).error !== null
  );
}

/**
 * Single fetch chokepoint. Always sends the same-origin httpOnly cookie.
 * On !ok, parses the {error:{code,message,details?}} envelope and throws ApiError.
 * On 401 (except the login path) fires the registered unauthorized handler, then
 * still throws so the caller can react.
 */
export async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  if (opts.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  let res: Response;
  try {
    res = await fetch(path, {
      ...opts,
      credentials: "same-origin",
      headers,
    });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Something went wrong. Please try again.");
  }

  if (res.ok) {
    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  // Parse the error envelope defensively (a 5xx/HTML body may not be JSON).
  let code = "INTERNAL_ERROR";
  let message = "Something went wrong. Please try again.";
  let details: unknown;
  try {
    const body: unknown = await res.json();
    if (isErrorEnvelope(body)) {
      code = body.error.code;
      message = body.error.message;
      details = body.error.details;
    }
  } catch {
    // keep defaults
  }

  if (res.status === 401 && path !== LOGIN_PATH) {
    const handler = getUnauthorizedHandler();
    handler?.();
  }

  throw new ApiError(res.status, code, message, details);
}
